import { create } from "zustand";
import { persist } from "zustand/middleware";
import projectsService, {
  Project,
  CreateProjectData,
} from "../services/projects.service";
import { indexedDB } from "./indexedDB.handler";

const emptyProjectAnalysisState = {
  analysisReady: false, selectedFileIds: [], analysisData: null, backendResult: null, currentStep: "",
  fileCount: 0, uploadedFileNames: [], selectedFiles: [], projectFiles: [], hasStartedAnalysis: false,
  activeStep: null, isProcessing: false, stepResults: {}, stepResultKeys: [], completedSteps: [],
  analysisError: "", lastQcPayload: null, activeRunId: null,
};

interface ProjectState {
  // Project Management State
  projects: Project[];
  selectedProject: Project | null;
  loading: boolean;
  error: string | null;
  refreshTrigger: number;
  qcSeed: number;
  setQcSeed: (seed: number) => void;
  // UI State (from app.context)
  activeTab: string;
  analysisReady: boolean;
  selectedFileIds: string[];
  analysisData: any;
  backendResult: any;
  currentStep: string;

  // Analysis/Workflow State (from projectAnalysis.context)
  fileCount: number;
  uploadedFileNames: string[];
  selectedFiles: string[];
  projectFiles: any[];
  hasStartedAnalysis: boolean;
  activeStep: string | null;
  isProcessing: boolean;
  stepResults: Record<string, any>;
  stepResultKeys: string[];
  completedSteps: string[];
  analysisError: string;
  lastQcPayload: any | null;
  activeRunId: string | null;
  setLastQcPayload: (payload: any) => void;
  setActiveRunId: (runId: string | null) => void;
  selectedGene: string;
  setSelectedGene: (gene: string) => void;

  // Project Management Actions
  fetchProjects: (auth: { accessToken: string }) => Promise<void>;
  createProject: (
    auth: { accessToken: string },
    projectData: CreateProjectData,
  ) => Promise<Project | null>;
  updateProject: (
    auth: { accessToken: string },
    projectId: number,
    projectData: CreateProjectData,
  ) => Promise<Project | null>;
  deleteProject: (
    auth: { accessToken: string },
    projectId: number,
  ) => Promise<boolean>;
  selectProject: (project: Project | null) => void;
  refreshProjects: () => void;
  clearError: () => void;
  reset: () => void;
  resetProjectState: () => void;
  resetForUser: () => void;

  // UI Actions
  setActiveTab: (tab: string) => void;
  setAnalysisReady: (ready: boolean) => void;
  setSelectedFileIds: (ids: string[]) => void;
  setAnalysisData: (data: any) => void;
  setBackendResult: (result: any) => void;
  setCurrentStep: (step: string) => void;
  setLoading: (loading: boolean) => void;

  // Analysis/Workflow Actions
  setFileCount: (count: number | ((prev: number) => number)) => void;
  setUploadedFileNames: (
    names: string[] | ((prev: string[]) => string[]),
  ) => void;
  setSelectedFiles: (files: string[] | ((prev: string[]) => string[])) => void;
  setProjectFiles: (files: any[] | ((prev: any[]) => any[])) => void;
  setHasStartedAnalysis: (
    started: boolean | ((prev: boolean) => boolean),
  ) => void;
  setActiveStep: (
    step: string | null | ((prev: string | null) => string | null),
  ) => void;
  setIsProcessing: (processing: boolean | ((prev: boolean) => boolean)) => void;
  setStepResultKeys: (keys: string[] | ((prev: string[]) => string[])) => void;
  loadStepResults: (keys: string[]) => Promise<void>;
  saveStepResult: (stepId: string, data: any) => Promise<void>;
  deleteStepResult: (stepId: string) => Promise<void>;
  setCompletedSteps: (steps: string[] | ((prev: string[]) => string[])) => void;
  setAnalysisError: (error: string | ((prev: string) => string)) => void;
  resetProjectAnalysis: () => void;


}

export const useProjectStore = create<ProjectState>()(
  persist(
    (set, get) => ({
      // Project Management Initial State
      projects: [],
      selectedProject: null,
      loading: false,
      error: null,
      refreshTrigger: 0,
      qcSeed: 0,
      setQcSeed: (seed: number) => set({ qcSeed: seed }),

      // UI Initial State
      activeTab: "projects",
      analysisReady: false,
      selectedFileIds: [],
      analysisData: null,
      backendResult: null,
      currentStep: "",
      selectedGene: "AL627309.1", // Default gene
      setSelectedGene: (gene: string) => set({ selectedGene: gene }),

      // Analysis/Workflow Initial State
      fileCount: 0,
      uploadedFileNames: [],
      selectedFiles: [],
      projectFiles: [],
      hasStartedAnalysis: false,
      activeStep: null,
      isProcessing: false,
      stepResults: {},
      stepResultKeys: [],
      completedSteps: [],
      analysisError: "",
      lastQcPayload: null,
      activeRunId: null,
      setActiveRunId: (runId) => set({ activeRunId: runId }),
      

      // Actions
      fetchProjects: async (auth) => {
        set({ loading: true, error: null });
        try {
          const projects = await projectsService.fetchProjectCards(auth);
          set((state) => ({
            projects,
            loading: false,
            // Preserve selectedProject if it still exists in the fetched projects
            selectedProject:
              state.selectedProject &&
              projects.find((p) => p.id === state.selectedProject?.id)
                ? state.selectedProject
                : null,
          }));
        } catch (error: any) {
          set({
            error: error.message || "Failed to fetch projects",
            loading: false,
          });
        }
      },

      createProject: async (auth, projectData) => {
        set({ loading: true, error: null });
        try {
          const newProject = await projectsService.createProject(
            auth,
            projectData,
          );
          set((state) => ({
            projects: [...state.projects, newProject],
            ...emptyProjectAnalysisState,
            selectedProject: newProject,
            loading: false,
          }));
          return newProject;
        } catch (error: any) {
          set({
            error: error.message || "Failed to create project",
            loading: false,
          });
          return null;
        }
      },

      updateProject: async (auth, projectId, projectData) => {
        set({ loading: true, error: null });
        try {
          const updatedProject = await projectsService.updateProject(
            auth,
            projectId,
            projectData,
          );
          set((state) => ({
            projects: state.projects.map((p) =>
              p.id === projectId ? updatedProject : p,
            ),
            selectedProject:
              state.selectedProject?.id === projectId
                ? updatedProject
                : state.selectedProject,
            loading: false,
          }));
          return updatedProject;
        } catch (error: any) {
          set({
            error: error.message || "Failed to update project",
            loading: false,
          });
          return null;
        }
      },

      deleteProject: async (auth, projectId) => {
        set({ loading: true, error: null });
        try {
          await projectsService.deleteProject(auth, projectId);
          set((state) => ({
            projects: state.projects.filter((p) => p.id !== projectId),
            selectedProject:
              state.selectedProject?.id === projectId
                ? null
                : state.selectedProject,
            loading: false,
          }));
          return true;
        } catch (error: any) {
          set({
            error: error.message || "Failed to delete project",
            loading: false,
          });
          return false;
        }
      },

      selectProject: (project) => {
        const previousId = get().selectedProject?.id;
        if (previousId === project?.id) {
          set({ selectedProject: project });
          return;
        }
        set({ ...emptyProjectAnalysisState, selectedProject: project });
        if (project) {
          const projectId = String(project.id);
          void (async () => {
            try {
              const keys = await indexedDB.getAllStepResultKeys(projectId);
              const results = await indexedDB.getStepResults(projectId, keys);
              if (String(get().selectedProject?.id) !== projectId) return;
              set((state) => ({
                stepResults: { ...results, ...state.stepResults },
                stepResultKeys: Array.from(new Set([...keys, ...state.stepResultKeys])),
                completedSteps: Array.from(new Set([...keys, ...state.completedSteps])),
                hasStartedAnalysis: keys.length > 0 || state.hasStartedAnalysis,
              }));
            } catch (error) {
              console.error("Failed to restore project analysis state:", error);
            }
          })();
        }
      },

      refreshProjects: () => {
        set((state) => ({ refreshTrigger: state.refreshTrigger + 1 }));
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set({
          projects: [],
          selectedProject: null,
          loading: false,
          error: null,
          refreshTrigger: 0,
        });
      },

      resetProjectState: () => {
        // Reset only project-specific state, keep projects list
        set({
          selectedProject: null,
          loading: false,
          error: null,
        });
      },

      resetForUser: () => {
        set({
          projects: [],
          selectedProject: null,
          loading: false,
          error: null,
          activeTab: "projects",
          analysisReady: false,
          selectedFileIds: [],
          analysisData: null,
          backendResult: null,
          currentStep: "",
          fileCount: 0,
          uploadedFileNames: [],
          selectedFiles: [],
          projectFiles: [],
          hasStartedAnalysis: false,
          activeStep: null,
          isProcessing: false,
          stepResults: {},
          stepResultKeys: [],
          completedSteps: [],
          analysisError: "",
          lastQcPayload: null,
          activeRunId: null,
        });
      },

      // UI Actions
      setActiveTab: (tab) => set({ activeTab: tab }),
      setAnalysisReady: (ready) => set({ analysisReady: ready }),
      setSelectedFileIds: (ids) => set({ selectedFileIds: ids }),
      setAnalysisData: (data) => set({ analysisData: data }),
      setBackendResult: (result) => set({ backendResult: result }),
      setLastQcPayload: (payload) => set({ lastQcPayload: payload }),
      setCurrentStep: (step) => set({ currentStep: step }),
      setLoading: (loading) => set({ loading }),

      // Analysis/Workflow Actions
      setFileCount: (count) =>
        set({
          fileCount:
            typeof count === "function" ? count(get().fileCount) : count,
        }),
      setUploadedFileNames: (names) =>
        set({
          uploadedFileNames:
            typeof names === "function"
              ? names(get().uploadedFileNames)
              : names,
        }),
      setSelectedFiles: (files) =>
        set({
          selectedFiles:
            typeof files === "function" ? files(get().selectedFiles) : files,
        }),
      setProjectFiles: (files) =>
        set({
          projectFiles:
            typeof files === "function" ? files(get().projectFiles) : files,
        }),
      setHasStartedAnalysis: (started) =>
        set({
          hasStartedAnalysis:
            typeof started === "function"
              ? started(get().hasStartedAnalysis)
              : started,
        }),
      setActiveStep: (step) =>
        set({
          activeStep:
            typeof step === "function" ? step(get().activeStep) : step,
        }),
      setIsProcessing: (processing) =>
        set({
          isProcessing:
            typeof processing === "function"
              ? processing(get().isProcessing)
              : processing,
        }),
      setStepResultKeys: (keys) =>
        set({
          stepResultKeys:
            typeof keys === "function" ? keys(get().stepResultKeys) : keys,
        }),
      loadStepResults: async (keys) => {
        try {
          const projectId = get().selectedProject?.id;
          if (projectId == null) return;
          const requestedProjectId = String(projectId);
          const results = await indexedDB.getStepResults(requestedProjectId, keys);
          if (String(get().selectedProject?.id) !== requestedProjectId) return;
          set((state) => ({ stepResults: { ...results, ...state.stepResults } }));
        } catch (error) {
          console.error("Failed to load step results from IndexedDB:", error);
        }
      },
      saveStepResult: async (stepId, data) => {
        try {
          const projectId = get().selectedProject?.id;
          if (projectId == null) return;
          await indexedDB.saveStepResult(String(projectId), stepId, data);
          set((state) => ({
            stepResults: { ...state.stepResults, [stepId]: data },
            stepResultKeys: state.stepResultKeys.includes(stepId)
              ? state.stepResultKeys
              : [...state.stepResultKeys, stepId],
          }));
        } catch (error) {
          console.error("Failed to save step result to IndexedDB:", error);
        }
      },
      deleteStepResult: async (stepId) => {
        try {
          const projectId = get().selectedProject?.id;
          if (projectId == null) return;
          await indexedDB.deleteStepResult(String(projectId), stepId);
          set((state) => {
            const newResults = { ...state.stepResults };
            delete newResults[stepId];
            return {
              stepResults: newResults,
              stepResultKeys: state.stepResultKeys.filter((key: string) => key !== stepId),
            };
          });
        } catch (error) {
          console.error("Failed to delete step result from IndexedDB:", error);
        }
      },
      setCompletedSteps: (steps) =>
        set({
          completedSteps:
            typeof steps === "function" ? steps(get().completedSteps) : steps,
        }),
      setAnalysisError: (error) =>
        set({
          analysisError:
            typeof error === "function" ? error(get().analysisError) : error,
        }),
      resetProjectAnalysis: async () => {
        try {
          const projectId = get().selectedProject?.id;
          if (projectId != null) {
            await indexedDB.clearProjectStepResults(String(projectId));
          }
        } catch (error) {
          console.error("Failed to clear step results from IndexedDB:", error);
        }
        set({
          fileCount: 0,
          uploadedFileNames: [],
          selectedFiles: [],
          projectFiles: [],
          hasStartedAnalysis: false,
          activeStep: null,
          isProcessing: false,
          stepResults: {},
          stepResultKeys: [],
          completedSteps: [],
          analysisError: "",
          lastQcPayload: null,
          activeRunId: null,
          
        });
      },
    }),
    {
      name: "project-storage",
      partialize: (state) => ({
        // Project Management
        selectedProject: state.selectedProject,
        
        // UI State
        activeTab: state.activeTab,
        analysisReady: state.analysisReady,
        selectedFileIds: state.selectedFileIds,
        analysisData: state.analysisData,
        // backendResult: state.backendResult,
        backendResult: state.backendResult
          ? {
              currentStep: state.backendResult.currentStep,
              qcArtifactId: state.backendResult.qcArtifactId,
              qcMeta: state.backendResult.qcMeta,
              selectedGene: state.selectedGene,
            }
          : null,
        currentStep: state.currentStep,

        // Analysis/Workflow State
        fileCount: state.fileCount,
        uploadedFileNames: state.uploadedFileNames,
        selectedFiles: state.selectedFiles,
        projectFiles: state.projectFiles,
        hasStartedAnalysis: state.hasStartedAnalysis,
        activeStep: state.activeStep,
        isProcessing: state.isProcessing,
        stepResultKeys: state.stepResultKeys,
        completedSteps: state.completedSteps,
        analysisError: state.analysisError,
        activeRunId: state.activeRunId,
      }),
    },
  ),
);
